import Footer from './jsx/components/beyond_gdp/Footer.jsx';
import PrincipleCards from './jsx/components/beyond_gdp/PrincipleCards.jsx';
import PREJUDICE_COUNTRY_NAMES from './jsx/components/beyond_gdp/prejudiceCountryNames.js';
import Relay from './jsx/components/beyond_gdp/Relay.jsx';
import SectionPhotoBand from './jsx/components/beyond_gdp/SectionPhotoBand.jsx';
import { DEVELOPMENT_STATUS_PALETTE_VARS, PAIR_PALETTE_VARS } from './jsx/components/charts/chartColors.js';

<Header title={props.meta.hero.title} subtitle={props.meta.hero.subtitle} byline={props.meta.hero.byline} image_url={props.meta.hero.image_url} titleHighlight={props.meta.hero.titleHighlight} downloadUrl={props.meta.download_report_url} />

{/* TODO: download_report_url in meta.json is a placeholder (unctad.org) until the client provides the real report link. */}
<ProgressBar selector=".bgdp_main" />
<Nav items={props.meta.nav} downloadUrl={props.meta.download_report_url} />
<BackToTop selector=".bgdp_header" />

<div className="bgdp_main">
<div className="bgdp_chapter bgdp_chapter--intro">
<div className="container_section">
<div className="container_section__wide bgdp_lead">

### Why go beyond GDP?

Gross domestic product (GDP) has long been the leading indicator used to measure progress and assess countries' performance.

GDP measures economic output. But it doesn't show whether people are living well, whether production and consumption are harming the planet, whether opportunities are shared or whether progress can continue over time.

These limitations are reflected in real-world outcomes. GDP growth has frequently been accompanied by persistent inequality, ecological degradation and declining public trust in institutions.

Episodic shocks, such as financial crises and the COVID-19 pandemic, have revealed systemic weaknesses. Long-term structural pressures, including environmental degradation, inequality, geopolitical tensions and democratic challenges, have further underscored the limitations of relying on GDP alone.

<StatTiles tiles={[{ value: 31, label: 'Indicators', url: 'https://www.un.org/sites/un2.un.org/files/high-level_expert_group_on_beyond_gdp_final_report.pdf#page=24' }, { value: 4, label: 'Dimensions', url: 'https://www.un.org/sites/un2.un.org/files/high-level_expert_group_on_beyond_gdp_final_report.pdf#page=20' }, { value: 2026, label: 'Country engagement', format: 'year', url: 'https://www.un.org/sites/un2.un.org/files/high-level_expert_group_on_beyond_gdp_final_report.pdf#page=35' }]} />

</div>
<Timeline
  title="Recent milestones on the way to Beyond GDP"
  milestones={[
    {
      date: 'September 2015',
      title: 'SDG target 17.19 adopted',
      label: (
        <>
          Under the <a href="https://sdgs.un.org/2030agenda" rel="noreferrer" target="_blank">2030 Agenda for Sustainable Development</a>, Sustainable Development Goal (SDG) target 17.19 aims to "By 2030, build on existing initiatives to develop measurements of progress on sustainable development that complement gross domestic product, and support statistical capacity-building in developing countries."
        </>
      )
    },
    {
      date: 'September 2024',
      title: 'Pact for the Future',
      label: (
        <>
          With the <a href="https://www.un.org/sites/un2.un.org/files/sotf-pact_for_the_future_adopted.pdf" rel="noreferrer" target="_blank">Pact for the Future</a>, UN Member States asked the UN Secretary-General to convene a <a href="https://www.un.org/en/beyondGDP" rel="noreferrer" target="_blank">High-Level Expert Group on Beyond GDP</a> to "develop recommendations for a limited set of country-owned, universally applicable indicators of sustainable development that complement and go beyond gross domestic product (GDP)".
        </>
      )
    },
    {
      date: '7 May 2026',
      title: 'Final report released',
      label: (
        <>
          The expert group delivered its report, <a href="https://unctad.org/publication/counting-what-counts-compass-progress-people-and-planet">Counting what counts: A compass of progress for people and planet</a>. The UN Department of Economic and Social Affairs (DESA), UN Trade and Development (UNCTAD), the UN Development Programme (UNDP) and the Executive Office of the Secretary-General served as the co-secretariat.
        </>
      )
    },
    {
      date: 'June 2026',
      title: 'Country engagement begins',
      label: 'In coordination with the Executive Office of the Secretary-General, UNCTAD and UNDP began engaging with the first countries to experiment with the metrics and collect feedback on their feasibility and relevance in national contexts, to help shape the refinement of global indicators, a process led by DESA under the UN Statistical Commission. At the same time, an intergovernmental process was initiated in preparation for the 81st UN General Assembly to agree on global norms for measuring progress.'
    }
  ]}
/>
</div>

<div className="container_section">
<div className="container_section__wide">

### What does going beyond GDP mean?

It requires a new understanding of what we mean by progress.

Progress builds on the foundational principles of peace, human rights and respect for the planet, recognizing that:

<PrincipleCards items={[{ icon: 'community.svg', title: 'Well-being is collective as well as individual' }, { icon: 'respect_for_the_planet.svg', title: 'People and planet are deeply interconnected' }, { icon: 'global.svg', title: 'Cross-border spillovers matter in an interdependent world' }]} />

The proposed dashboard contains 31 indicators designed to help countries track progress beyond GDP:

<ul className="bullet_list">
  <li>Around half of the indicators are drawn directly from the Sustainable Development Goals (SDGs), so countries can start immediately</li>
  <li>It is country-owned and adaptable</li>
  <li>It measures well-being outcomes, equity and inclusion directly</li>
  <li>It tracks sustainability and resilience through natural, human, social and institutional capital</li>
</ul>

</div>
</div>
</div>

<div className="bgdp_chapter bgdp_chapter--data">
<ChapterBanner anchorClass="anchor_data" eyebrow="Insights" title="What the selected data show" />
<SectionPhotoBand image="assets/img/divider_hands_circle.jpg" />

<SectionDivider tint={false}>

The dashboard changes the questions we ask about progress. These examples below compare economic output with outcomes for people, society and the planet, highlighting insights and data gaps from indicators in the new dashboard for tracking progress beyond GDP.

</SectionDivider>

<DashboardFramework
  title="The Beyond GDP dashboard tracks three dimensions of progress supported by foundational principles"
  foundationalPrinciples={[
    { key: 'peace', icon: 'peace', label: 'Peace' },
    { key: 'human_rights', icon: 'human_rights', label: 'Human Rights' },
    { key: 'planet', icon: 'planet', label: 'Respect for the Planet' }
  ]}
  dimensions={[
    { key: 'current_wellbeing', label: 'Current well-being', variant: 'yellow', components: [{ label: 'Material conditions and work', icon: 'material_conditions_and_work' }, { label: 'Health', href: '.anchor_health', icon: 'health' }, { label: 'Education', icon: 'education' }, { label: 'Security', href: '.anchor_security', icon: 'security' }, { label: 'Subjective well-being', icon: 'subjective_well_being' }, { label: 'Social cohesion', href: '.anchor_trust', icon: 'social_cohesion' }, { label: 'Quality of institutions', icon: 'quality_of_institutions' }, { label: 'Environmental quality', icon: 'environmental_quality' }] },
    { key: 'equity_inclusion', label: 'Equity and inclusion', variant: 'purple', components: [{ label: 'Wealth inequality', href: '.anchor_wealth_inequality', icon: 'wealth_inequality' }, { label: 'Income inequality', href: '.anchor_wage_gap', icon: 'income_inequality' }, { label: 'Poverty', icon: 'poverty' }, { label: 'Work inclusion', icon: 'work_inclusion' }, { label: 'Regional inequalities', icon: 'regional_inequalities' }, { label: 'Overlapping deprivations', href: '.anchor_prejudice', icon: 'overlapping_deprivations' }] },
    { key: 'sustainability_resilience', label: 'Sustainability and resilience', variant: 'green', components: [{ label: 'Produced capital', icon: 'produced_capital' }, { label: 'Human capital', icon: 'human_capital' }, { label: 'Social capital', icon: 'social_capital' }, { label: 'Institutional capital', icon: 'institutional_capital' }, { label: 'Natural capital', href: '.anchor_emissions', icon: 'natural_capital' }] }
  ]}
/>

<SectionDivider anchorClass="anchor_current_wellbeing" image="assets/img/divider_couple.jpg" tint={false} title="Current well-being">

This dimension covers material conditions and work, health, education, security, subjective well-being, social cohesion, the quality of institutions and environmental quality. Let's look at some selected data.

</SectionDivider>

<div className="bgdp_chart_strip">

<ChartData file="2026-beyond_gdp_healthy_life_expectancy.json">
{data => (
  <ChartSection
    anchorClass="anchor_health"
    dimensionChip={{ icon: 'health', label: 'Health', variant: 'yellow' }}
    title="Higher incomes don't always mean longer, healthier lives"
    description="Median gross domestic product (GDP) per capita and healthy life expectancy in years, Northern America, Northern Europe and Southern Europe, dollars and years, 2000–2019."
    source="UN Trade and Development (UNCTAD) based on World Bank and WHO."
    insight={["Higher GDP doesn't always translate into more well-being. Southern Europe's GDP per capita is less than one half of Northern America's and Northern Europe's. Yet its healthy life expectancy is higher than the former's and has caught up to the latter's. In Southern Europe, healthy life expectancy continued to improve despite economic slowdown."]}
  >
    <ChartPair
      rightDelayMs={1400}
      leftChart={(vis, hover) => <LineChartMulti data={data} seriesKey="region" valueKey="gdp_per_capita" isVisible={vis} title="Gross domestic product (GDP) per capita, dollars" yLabel="Gross domestic product (GDP) per capita, dollars" hoveredYear={hover.hoveredYear} onHoverChange={hover.onHoverChange} palette={['--un-color-blue-brand', '--un-color-blue-darkest', '--un-color-yellow']} series={[{ key: 'NAM', label: 'Northern America' }, { key: 'NEU', label: 'Northern Europe' }, { key: 'SEU', label: 'Southern Europe' }]} />}
      rightChart={(vis, hover) => <LineChartMulti data={data} seriesKey="region" valueKey="healthy_life_exp" isVisible={vis} title="Healthy life expectancy, years" yLabel="Healthy life expectancy, years" yMin={64} yMax={72} hoveredYear={hover.hoveredYear} onHoverChange={hover.onHoverChange} palette={['--un-color-blue-brand', '--un-color-blue-darkest', '--un-color-yellow']} series={[{ key: 'NAM', label: 'Northern America' }, { key: 'NEU', label: 'Northern Europe' }, { key: 'SEU', label: 'Southern Europe' }]} />}
    />
  </ChartSection>
)}
</ChartData>

<ChartData file="2026-beyond_gdp_homicides.json">
{data => (
  <ChartSection
    anchorClass="anchor_security"
    dimensionChip={{ icon: 'security', label: 'Security', variant: 'yellow' }}
    title="Safety and security can worsen even as GDP improves"
    description="Median gross domestic product (GDP) per capita and intentional homicides, Latin America and the Caribbean and Eastern and South-Eastern Asia, index: 2015=100 and homicides per 100,000 people, 2015–2023."
    insight={["Sustained GDP growth doesn't always deliver more safety. In Latin America, homicides increased nearly 50% from 2015 to 2023, even as GDP rose 17%. In fact, median homicide rates reached their highest level on record in 2023. This contrasts sharply with Eastern and South-Eastern Asia, where homicide rates were 29 times lower."]}
    expandable={{ label: 'Countries in calculation', items: ['Eastern and South-Eastern Asia: Japan, Malaysia, Mongolia, Myanmar, Republic of Korea, Singapore.', 'Latin America: Argentina, Barbados, Brazil, Chile, Colombia, Costa Rica, Dominica, Ecuador, Grenada, Guyana, Honduras, Jamaica, Mexico, Panama, Paraguay, Plurinational State of Bolivia, Saint Lucia, Suriname, Uruguay.'] }}
    source="World Bank."
  >
    <ChartPair
      rightDelayMs={1400}
      leftChart={(vis, hover) => <LineChartMulti data={data} seriesKey="UNCTAD_region" valueKey="gdp_per_capita" isVisible={vis} title="Gross domestic product (GDP) per capita index, index 2015=100" yLabel="Gross domestic product (GDP) per capita, index 2015=100" yMin={90} yMax={121} hoveredYear={hover.hoveredYear} onHoverChange={hover.onHoverChange} series={[{ key: 'Latin America and the Caribbean', label: 'Latin America and the Caribbean' }, { key: 'Eastern and South-Eastern Asia', label: 'Eastern and South-Eastern Asia' }]} />}
      rightChart={(vis, hover) => <LineChartMulti data={data} seriesKey="UNCTAD_region" valueKey="homicides" isVisible={vis} title="Number of victims of homicide per 100 000 people" yLabel="Number of victims of homicide per 100 000 people" yMin={0} yMax={20} hoveredYear={hover.hoveredYear} onHoverChange={hover.onHoverChange} series={[{ key: 'Latin America and the Caribbean', label: 'Latin America and the Caribbean' }, { key: 'Eastern and South-Eastern Asia', label: 'Eastern and South-Eastern Asia' }]} />}
    />
  </ChartSection>
)}
</ChartData>

<ChartData file="2026-beyond_gdp_trust.json">
{data => (
  <ChartSection
    anchorClass="anchor_trust"
    dimensionChip={{ icon: 'social_cohesion', label: 'Social cohesion', variant: 'yellow' }}
    title="Economic growth doesn't automatically improve social cohesion"
    description="Median gross domestic product (GDP) per capita and share of people who say most people can be trusted, Northern Africa and Western Asia, index: 2014=100 and percentage, 2014 and 2022."
    source="World Bank and World Values Survey."
    insight={["You can't buy trust. Similar economic growth can coincide with different changes in social cohesion. Median GDP rose 10.5% in Northern Africa between 2014 and 2022, but trust in people fell more than 17%. Meanwhile, both GDP per capita and trust rose by more than 20% during the same period in Western Asia."]}
    expandable={{ label: 'Countries in calculation', items: ['Northern Africa: Egypt, Libya, Morocco, Tunisia.', 'Western Asia: Armenia, Azerbaijan, Georgia, Iraq, Jordan, Lebanon, Türkiye.'] }}
  >
    <ChartPair
      rightDelayMs={1400}
      leftChart={vis => (
        <GroupedBarChart
          isVisible={vis}
          title="Gross domestic product (GDP) per capita, index 2014=100"
          yLabel="Gross domestic product (GDP) per capita, index 2014=100"
          yMax={150}
          palette={PAIR_PALETTE_VARS}
          categories={[{ key: 'Northern Africa', label: 'Northern Africa' }, { key: 'Western Asia', label: 'Western Asia' }]}
          series={data.years.map(y => ({ key: String(y), label: String(y) }))}
          values={Object.fromEntries(Object.entries(data.regions).map(([region, r]) => [region, Object.fromEntries(data.years.map((y, i) => [String(y), r.gdp_index[i]]))]))}
        />
      )}
      rightChart={vis => (
        <GroupedBarChart
          isVisible={vis}
          title="Share of people who say most people can be trusted, per cent"
          yLabel="Share of people who say most people can be trusted, per cent"
          yMax={15}
          valueFormat=""
          palette={PAIR_PALETTE_VARS}
          categories={[{ key: 'Northern Africa', label: 'Northern Africa' }, { key: 'Western Asia', label: 'Western Asia' }]}
          series={data.years.map(y => ({ key: String(y), label: String(y) }))}
          values={Object.fromEntries(Object.entries(data.regions).map(([region, r]) => [region, Object.fromEntries(data.years.map((y, i) => [String(y), r.trust_pct[i]]))]))}
        />
      )}
    />
  </ChartSection>
)}
</ChartData>

<ChartData file="2026-beyond_gdp_satisfaction.json">
{data => (
  <ChartSection
    title="More prosperity doesn't guarantee more satisfaction with public services"
    description="Share of people satisfied with their last experience of public services and gross domestic product (GDP) per capita, selected countries, percentage and dollars, 2015–2024 average."
    source="World Bank and United Nations Development Programme via the United Nations Global SDG Indicators Database."
    note="Reported satisfaction may reflect expectancy-disconfirmation. People with higher expectations may report lower satisfaction, even when the objective quality of the service is higher."
    insight={["Confidence in institutions matters. And this doesn't always come with higher levels of economic prosperity. Bulgaria and Mexico report greater satisfaction with public services than some wealthier countries, such as Norway and Italy."]}
  >
    <DualBarRowsLoader data={data} />
  </ChartSection>
)}
</ChartData>

</div>

<SectionDivider anchorClass="anchor_equity_inclusion" image="assets/img/divider_buildings_sky.jpg" tint={false} title="Equity and inclusion">

This dimension measures whether opportunities and outcomes are shared. It covers wealth and income inequality, poverty, work inclusion, regional inequalities and overlapping deprivations. Let's look at some selected data.

</SectionDivider>

<div className="bgdp_chart_strip">

<ChartData file="2026-beyond_gdp_wealth_inequality.json">
{data => (
  <ChartSection
    anchorClass="anchor_wealth_inequality"
    dimensionChip={{ icon: 'wealth_inequality', label: 'Wealth inequality', variant: 'purple' }}
    title="Faster growth can come with wider wealth inequality"
    description="Median gross domestic product (GDP) per capita and share of wealth held by the bottom 99%, Eastern and Southern Europe, index: 2000=100 and percentage, 2000–2019."
    source="UN Trade and Development (UNCTAD) based on World Bank and World Inequality Database."
    insight={["A rising tide doesn't always lift all boats – economic growth can coincide with growing inequality. For example, GDP per capita grew faster in Eastern Europe than in Southern Europe. Yet the share of wealth held by the bottom 99% declined more sharply in Eastern Europe."]}
    expandable={{ label: 'Countries in calculation', items: ['Eastern Europe: Belarus, Bulgaria, Czechia, Hungary, Poland, Republic of Moldova, Romania, Russian Federation, Slovakia, Ukraine.', 'Southern Europe: Albania, Andorra, Bosnia and Herzegovina, Croatia, Cyprus, Greece, Italy, Malta, Montenegro, North Macedonia, Portugal, San Marino, Serbia, Slovenia, Spain.'] }}
  >
    <ChartPair
      rightDelayMs={1400}
      leftChart={(vis, hover) => <LineChartMulti data={data} seriesKey="UNCTAD_region" valueKey="gdp_per_capita" isVisible={vis} title="Gross domestic product (GDP) per capita, index 2000=100" yLabel="Gross domestic product (GDP) per capita, index 2000=100" yMin={90} yMax={250} hoveredYear={hover.hoveredYear} onHoverChange={hover.onHoverChange} series={[{ key: 'Eastern Europe', label: 'Eastern Europe' }, { key: 'Southern Europe', label: 'Southern Europe' }]} />}
      rightChart={(vis, hover) => <LineChartMulti data={data} seriesKey="UNCTAD_region" valueKey="wealth_99_perc" isVisible={vis} title="Wealth share of bottom 99%, per cent" yLabel="Wealth share of bottom 99%, percentage"  yMin={75} yMax={78} hoveredYear={hover.hoveredYear} onHoverChange={hover.onHoverChange} series={[{ key: 'Eastern Europe', label: 'Eastern Europe' }, { key: 'Southern Europe', label: 'Southern Europe' }]} />}
    />
  </ChartSection>
)}
</ChartData>

<ChartData file="2026-beyond_gdp_wage_gap.json">
{data => (
  <ChartSection
    anchorClass="anchor_wage_gap"
    dimensionChip={{ icon: 'income_inequality', label: 'Income inequality', variant: 'purple' }}
    title="Economic growth outpaces gains in women's pay"
    description="Average gross domestic product (GDP) per capita and women's average hourly earnings as a share of men's, developed economies, dollars and percentage, 2010–2013 and 2020–2023 averages."
    source="World Bank and International Labour Organization via the United Nations Global SDG Indicators Database."
    insight={["GDP growth can be slower for one segment of the population. Average GDP per capita in developed economies rose 12% over a 10-year period, but women's pay relative to men's improved only 1% – less than one tenth as much."]}
    expandable={{ label: 'Countries in calculation', items: ['Bosnia and Herzegovina, Czechia, Finland, France, Greece, Hungary, Italy, Portugal, Republic of Korea, Republic of Moldova, Romania, Slovakia, Spain, Sweden, Switzerland, United Kingdom of Great Britain and Northern Ireland, United States of America.'] }}
  >
    <ChartPair
      rightDelayMs={840}
      leftChart={vis => <BarPair isVisible={vis} title="Gross domestic product (GDP) per capita, dollars" yLabel="Gross domestic product (GDP) per capita, dollars" periods={data.periods} values={data.series.gdp_per_capita} valueFormat="" />}
      rightChart={vis => <BarPair isVisible={vis} title="Women's hourly earnings, share of men's, per cent" yLabel="Women's hourly earnings, share of men's" periods={data.periods} values={data.series.wage_gap_pct} valueFormat="percent" />}
    />
  </ChartSection>
)}
</ChartData>

<ChartData file="2026-beyond_gdp_prejudice.json">
{data => (
  <ChartSection
    anchorClass="anchor_prejudice"
    dimensionChip={{ icon: 'overlapping_deprivations', label: 'Overlapping deprivations', variant: 'purple' }}
    title="Economic prosperity doesn't protect people against prejudice"
    description="Gross domestic product (GDP) per capita and share of people reporting discrimination or harassment in the previous 12 months, selected countries, dollars on a logarithmic scale and percentage, 2014–2024 average."
    insight={["Higher GDP isn't systematically linked to less discrimination or harassment. Focusing only on economic growth creates a major blind spot in people's lived experience."]}
    source="World Bank and Office of the United Nations High Commissioner for Human Rights (OHCHR)."
    fitChart
  >
    <RevealOnView>
      {vis => (
        <ScatterLog
          points={data.map(d => ({ key: d.iso3, label: PREJUDICE_COUNTRY_NAMES[d.iso3] ?? d.iso3, x: d['GDP per capita (log scale)'], y: d['Share reporting discrimination or harassment'] }))}
          title="Gross domestic product (GDP) per capita vs. share reporting discrimination or harassment"
          xLabel="Gross domestic product (GDP) per capita, dollars"
          yLabel="Share reporting discrimination or harassment, per cent"
          isVisible={vis}
        />
      )}
    </RevealOnView>
  </ChartSection>
)}
</ChartData>

</div>

<SectionDivider anchorClass="anchor_sustainability_resilience" image="assets/img/divider_hourglass.jpg" tint={false} title="Sustainability and resilience">

This dimension tracks whether progress can continue over time. It considers produced, human, social, institutional and natural capital.

</SectionDivider>

<div className="bgdp_chart_strip">

<ChartData file="2026-beyond_gdp_emissions.json">
{data => (
  <ChartSection
    anchorClass="anchor_emissions"
    dimensionChip={{ icon: 'natural_capital', label: 'Natural capital', variant: 'green' }}
    title="Global emissions rise even as emissions per person level off"
    description="Greenhouse gas emissions, by development status, billions of tonnes of carbon dioxide equivalent and median tonnes of carbon dioxide equivalent per person, 2000–2017."
    source="UN Trade and Development (UNCTAD) based on UNFCCC Secretariat via UN Global SDG Indicators Database."
    note="Units assumed from the draft mockup (tonnes CO₂e per person; billions of tonnes CO₂e total) – not explicitly confirmed in the source graphic."
    insight={["The picture changes if we look at emissions per person or total emissions. Per-person emissions have stabilized in many countries, but economic and population growth continue to push total emissions higher. Staying within planetary boundaries requires greater ambition and action."]}
  >
    <ChartPair
      rightDelayMs={1400}
      leftChart={(vis, hover) => <LineChartMulti data={data} seriesKey="developing" valueKey="total_ghg_emissions" isVisible={vis} title="Total greenhouse gas (GHG) emissions, billions of tonnes CO₂e" yLabel="Total greenhouse gas (GHG) emissions, billions of tonnes CO₂e" yMax={30} hoveredYear={hover.hoveredYear} onHoverChange={hover.onHoverChange} stackedLegend palette={DEVELOPMENT_STATUS_PALETTE_VARS} series={[{ key: 'Developed', label: 'Developed' }, { key: 'LDCs', label: 'Least developed countries (LDCs)' }, { key: 'Other developing', label: 'Other developing' }]} />}
      rightChart={(vis, hover) => <LineChartMulti data={data} seriesKey="developing" valueKey="ghg_per_capita" isVisible={vis} title="Greenhouse gas (GHG) emissions per capita, tonnes CO₂e per person" yLabel="Greenhouse gas (GHG) emissions per capita, tonnes CO₂e per person" hoveredYear={hover.hoveredYear} onHoverChange={hover.onHoverChange} stackedLegend palette={DEVELOPMENT_STATUS_PALETTE_VARS} series={[{ key: 'Developed', label: 'Developed' }, { key: 'LDCs', label: 'Least developed countries (LDCs)' }, { key: 'Other developing', label: 'Other developing' }]} />}
    />
  </ChartSection>
)}
</ChartData>

</div>

</div>

<div className="bgdp_chapter bgdp_chapter--recommendation">
<ChapterBanner anchorClass="anchor_gaps" eyebrow="Data gaps" title="How can we fix what we can't see?" />
<SectionPhotoBand image="assets/img/divider_iceberg.jpg" />
<SectionDivider tint={false}>

The availability of Beyond GDP indicators varies widely across levels of development and dashboard dimensions.

</SectionDivider>

<div className="bgdp_chart_strip">

<ChartData file="2026-beyond_gdp_gap1.json">
{data => (
  <ChartSection
    title="Developing countries have less Beyond GDP data"
    description="Beyond GDP dashboard indicators with at least one publicly available data point, by dimension and development status, percentage."
    source="UN Trade and Development (UNCTAD) calculations on internationally available data"
    insight={["Although the analysis largely relies on the SDG indicator framework, data availability remains limited. Based on data collected in April 2026 for the available international indicators, coverage gaps between developed and developing countries are particularly pronounced for current well-being and sustainability and resilience. At present, fewer than half of developing countries are covered for these two key dimensions."]}
  >
    <GapChart data={data} title="Developing countries have less Beyond GDP data" yLabel="Indicators with available data, percentage" />
  </ChartSection>
)}
</ChartData>

</div>

<SectionDivider eyebrow="Who must act" tint={false} title="A shared effort to move beyond GDP">

Moving beyond GDP builds on decades of international efforts to incorporate sustainability and well-being in the way we measure and understand progress. In 1987, the World Commission on Environment and Development released <a href="http://www.un-documents.net/our-common-future.pdf" rel="noreferrer" target="_blank">Our Common Future</a> (the Brundtland Report), which popularized the concept of sustainable development by linking economic development with environment concerns. In 2009, the Stiglitz-Sen-Fitoussi report, <a href="https://ec.europa.eu/eurostat/documents/8131721/8131772/Stiglitz-Sen-Fitoussi-Commission-report.pdf" rel="noreferrer" target="_blank">Mismeasuring Our Lives: Why GDP Doesn't Add Up</a>, introduced an array of concepts, ranging from sustainable measures of economic welfare to measures of savings and wealth and a "green GDP." Together, they changed the way people think about progress, laying the foundation for the modern Beyond GDP agenda.

Turning the Beyond GDP agenda into practice requires coordinated action across countries, the UN system, the statistical community, civil society, and other stakeholders. Country engagement is central to adapting the framework to national priorities, strengthening data and statistical systems, and using it to inform decision-making. The high-level expert group's recommendations call on:

<Relay
  items={[
    { title: 'Governments', description: 'to invest in statistics, data and innovation' },
    { title: 'Countries', description: 'to set up national progress-measurement initiatives' },
    { title: 'The UN', description: 'to support implementation, including a global capacity development and reporting process' },
    { title: 'The statistical community', description: 'to improve methods and fill data gaps' },
    { title: 'Civil society and others', description: 'to support measurement and accountability' }
  ]}
/>

</SectionDivider>

</div>

<div className="bgdp_chapter bgdp_chapter--next">
<ChapterBanner anchorClass="anchor_next" eyebrow="Driving change" title="UNCTAD's role in Beyond GDP" />
<SectionDivider tint={false}>

UNCTAD's work on Beyond GDP focuses on supporting countries' statistical and analytical capacities, as requested at the UNCTAD16 conference.

We work closely with DESA, UNDP and the Executive Office of the Secretary-General on capacity support. We also contribute to methodology development under the Statistical Commission and the United Nations Chief Statisticians' network, and advance youth engagement with the Beyond Lab at UN Geneva.

We support the Global Alliance on Beyond GDP, launched in Sevilla and spearheaded by Spain. Together with the Organisation for Economic Co-operation and Development (OECD) and the Ibero-American General Secretariat (SEGIB), the Alliance facilitates country exchanges and learning.

We also work with countries and civil society organizations to ensure efficient support and promote shared global approaches.

</SectionDivider>

<RevealList
  layout="cards"
  items={[
    { title: 'Country capacity support', description: 'We work closely with DESA, UNDP and the Executive Office of the Secretary-General on capacity support.', icon: 'country.svg', links: [{ label: 'Learn more', url: 'https://unctad.org/statistics/beyond-gdp' }] },
    { title: 'Methodological collaboration', description: "We contribute to methodology development under the Statistical Commission and the United Nations Chief Statisticians' network.", icon: 'community.svg', links: [{ label: 'Learn more', url: 'https://unstats.un.org/UNSDWebsite/events-details/un57sc-beyond-gdp-rethinking-progress-in-a-changing-world-2Mar2026/' }] },
    { title: 'Youth engagement', description: 'We advance youth engagement with the Beyond Lab at UN Geneva.', icon: 'individual.svg', links: [{ label: 'Learn more', url: 'https://www.thebeyondlab.org/initiative/beyond-gdp' }] },
    { title: 'International exchange and partnership', description: 'We support the Global Alliance on Beyond GDP, launched in Sevilla and spearheaded by Spain. Together with the OECD and SEGIB, the Alliance facilitates country exchanges and learning.', icon: 'global.svg', links: [{ label: 'Learn more', url: 'https://unctad.org/meeting/fin4dev-dialogue-first-plenary-meeting-beyond-gdp-global-alliance' }] }
  ]}
/>

<p className="bgdp_disclaimer">This work is part of the project "Beyond GDP: Advancing global and national initiatives to develop, compile and integrate beyond-GDP metrics to foster inclusive, sustainable, and forward-thinking policy making" with financial support from the 2030 Agenda Sub-Fund of the United Nations Peace and Development Trust Fund.</p>

<Footer downloadUrl={props.meta.download_report_url} title="Moving beyond GDP requires a collective, long-term effort.">

Governments, institutions and society need stronger data and evidence-based action.

</Footer>
</div>
</div>
